import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import Spinner from '~/components/Spinner/Spinner';
import { fetchBuildingDetails } from '~/api/building';
import { fetchRetentionExpiry } from '~/api/analytics';
import { Building } from '~/types/building';
import { useSelector } from 'react-redux';
import { RootState } from '~/redux/store';
import dashboardClasses from '~/containers/Dashboard/Dashboard.module.css';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import moment from 'moment';
import { RetentionExpiryRow } from '~/types/analytics';
import styles from './RetainedLeases.module.css';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  YAxis,
  XAxis
} from 'recharts';

const RetainedLeases = () => {
  const { buildingId } = useParams<{ buildingId: string }>();
  // Keep auth state wired (may be used for future role-gating on this page)
  useSelector((state: RootState) => state.auth.userRole);

  const { data: buildingDetailsRes, isLoading: isBuildingDetailsLoading } =
    useQuery(['buildingDetails', buildingId], () => fetchBuildingDetails(buildingId), {
      refetchOnWindowFocus: false
    });

  const {
    data: retentionExpiryRes,
    isLoading: isRetentionExpiryLoading,
    isError: isRetentionExpiryError
  } = useQuery(
    ['retentionExpiry', buildingId],
    () => fetchRetentionExpiry(buildingId),
    {
      refetchOnWindowFocus: false,
      enabled: !!buildingId
    }
  );

  const building = buildingDetailsRes?.data as Building | undefined;
  const retentionRows = useMemo<RetentionExpiryRow[]>(() => {
    const raw = retentionExpiryRes?.data;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as RetentionExpiryRow[];
    // Endpoint may return a single object (e.g. mocked data). Normalize to array.
    return [raw as RetentionExpiryRow];
  }, [retentionExpiryRes?.data]);

  const formatPct = (v?: number) => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    return `${Math.round(v * 100)}%`;
  };

  const safeRate = (retained: number, expiries: number) => {
    if (!expiries || expiries <= 0) return 0;
    return retained / expiries;
  };

  const latestRow = useMemo(() => {
    if (!retentionRows.length) return null;
    return [...retentionRows].sort((a, b) => {
      return moment.utc(b.month).valueOf() - moment.utc(a.month).valueOf();
    })[0];
  }, [retentionRows]);

  const ttmSummary = useMemo(() => {
    if (!latestRow) return null;
    const totalExpiries = (latestRow.cobuExpiries || 0) + (latestRow.nonCobuExpiries || 0);
    const totalRetained = (latestRow.cobuRetained || 0) + (latestRow.nonCobuRetained || 0);
    const totalRate = safeRate(totalRetained, totalExpiries);
    return {
      monthLabel: moment.utc(latestRow.month).format('YYYY-MM'),
      totalExpiries,
      totalRetained,
      totalRate
    };
  }, [latestRow]);

  const chartData = useMemo(() => {
    if (!latestRow || !ttmSummary) return [];
    const nonCobuRate = safeRate(latestRow.nonCobuRetained || 0, latestRow.nonCobuExpiries || 0);
    const cobuRate = safeRate(latestRow.cobuRetained || 0, latestRow.cobuExpiries || 0);
    return [
      { key: 'nonCobu', name: 'Not Cobu Users', value: nonCobuRate, fill: '#FF0000' },
      { key: 'cobu', name: 'Cobu Users', value: cobuRate, fill: '#0B6B00' },
      { key: 'total', name: 'Total', value: ttmSummary.totalRate, fill: '#CFCFCF' }
    ];
  }, [latestRow, ttmSummary]);

  const analysisPeriodLabel = useMemo(() => {
    const toYYYYMM = (value: string) => {
      // Accept either "YYYY-MM" or ISO timestamp.
      if (/^\d{4}-\d{2}$/.test(value)) return value;
      const parsed = moment.utc(value);
      return parsed.isValid() ? parsed.format('YYYY-MM') : value;
    };

    // Start: prefer explicit backend-provided window start; otherwise derive from oldest row we have.
    const start =
      latestRow?.dataStartMonth ||
      (retentionRows.length
        ? moment
            .utc(
              [...retentionRows].sort(
                (a, b) => moment.utc(a.month).valueOf() - moment.utc(b.month).valueOf()
              )[0].month
            )
            .format('YYYY-MM')
        : null);

    // End: prefer backend-provided expiryEnd; otherwise fall back to latest row month.
    const end =
      latestRow?.expiryEnd || (latestRow ? moment.utc(latestRow.month).format('YYYY-MM') : null);

    if (!start || !end) return null;
    return `Analysis period: ${toYYYYMM(start)} to ${toYYYYMM(end)}`;
  }, [latestRow?.dataStartMonth, latestRow?.expiryEnd, latestRow?.month, retentionRows]);

  if (isBuildingDetailsLoading) {
    return (
      <div className={dashboardClasses.spinner}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className='content-container'>
      <Breadcrumbs />

      <div className={styles.buildingName}>{building?.name || ''}</div>

      {isRetentionExpiryLoading ? (
        <div className='margin-top-16'>Loading retention expiry…</div>
      ) : isRetentionExpiryError ? (
        <div className='margin-top-16'>Failed to load retention expiry.</div>
      ) : !latestRow || !ttmSummary ? (
        <div className='margin-top-16'>No retention data is available for this building yet.</div>
      ) : (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Resident Retention Rate</div>
          <div style={{ width: '100%', height: 420 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 44, right: 20, left: 20, bottom: 10 }}>
                <CartesianGrid stroke="#D9D9D9" />
                <YAxis
                  // Add headroom so 100% bars don't clip the label at the top
                  domain={[0, 1.05]}
                  ticks={[0, 0.25, 0.5, 0.75, 1]}
                  tickFormatter={(v) => `${Math.round(v * 100)}%`}
                />
                <XAxis dataKey="name" tick={{ fontSize: 22 }} />
                <Bar dataKey="value">
                  {chartData.map((entry) => (
                    <Cell key={entry.key} fill={entry.fill} />
                  ))}
                  <LabelList
                    dataKey="value"
                    position="top"
                    offset={12}
                    formatter={(v: any) => `${Math.round(Number(v) * 100)}%`}
                    style={{ fill: '#000', fontSize: 26, fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <table className={styles.summaryTable}>
            <thead>
              <tr>
                <th />
                <th>Not Cobu Users</th>
                <th>Cobu Users</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={styles.rowLabel}>Expiring Leases</td>
                <td>{latestRow.nonCobuExpiries}</td>
                <td>{latestRow.cobuExpiries}</td>
                <td>{ttmSummary.totalExpiries}</td>
              </tr>
              <tr>
                <td className={styles.rowLabel}>Retained</td>
                <td>{latestRow.nonCobuRetained}</td>
                <td>{latestRow.cobuRetained}</td>
                <td>{ttmSummary.totalRetained}</td>
              </tr>
            </tbody>
          </table>

          <div className={styles.ttm}>
            {analysisPeriodLabel || 'Analysis period unavailable'}
          </div>
          <div className={styles.analysisDescription}>
            <strong>Analysis Description:</strong> This analysis looks at renewal outcomes for residents with expiring leases and compares Cobu users to non-users. A resident is considered retained if they have a new lease or move-in within 60 days of lease end. Records without an email address or lease end date are excluded.  While many factors influence renewal decisions, the data shows higher renewal rates among residents who use Cobu.
          </div>
        </div>
      )}

    
    </div>
  );
};

export default RetainedLeases;


